/**
 * Portail client (Phase C) — page PUBLIQUE `/portail/:token`.
 *
 * Le client (non-utilisateur de la plateforme) prouve la possession de son e-mail
 * via un code (OTP) reçu par e-mail, puis consulte EN LECTURE SEULE l'avancement
 * de son dossier. Aucune action juridictionnelle, aucun autre dossier accessible.
 */
import { useState } from "react";
import { useParams } from "wouter";
import { toast } from "sonner";
import { Loader2, ShieldCheck, FileText } from "lucide-react";

import {
  demanderAccesPortail,
  verifierPortail,
  getPortailDonnees,
  type PortailDonnees,
} from "@/lib/portailApi";

const GOLD = "oklch(0.695 0.105 68)";
const BORDER = "color-mix(in oklch, oklch(0.695 0.105 68) 18%, transparent)";
const inputCls = "mt-1 block w-full rounded-md border px-3 py-2 text-sm bg-transparent";

type Step = "email" | "code" | "data";

export default function PortailClient() {
  const params = useParams();
  const token = params.token ?? "";

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState<PortailDonnees | null>(null);

  async function onAskCode() {
    if (!/.+@.+\..+/.test(email)) {
      toast.error("Saisissez une adresse e-mail valide.");
      return;
    }
    setBusy(true);
    try {
      await demanderAccesPortail(token, email.trim());
      toast.success("Si l'adresse correspond, un code vient d'être envoyé.");
      setStep("code");
    } catch {
      toast.error("Demande momentanément indisponible.");
    } finally {
      setBusy(false);
    }
  }

  async function onVerify() {
    if (code.trim().length < 4) {
      toast.error("Saisissez le code reçu par e-mail.");
      return;
    }
    setBusy(true);
    try {
      const { session } = await verifierPortail(token, email.trim(), code.trim());
      const donnees = await getPortailDonnees(session);
      setData(donnees);
      setStep("data");
    } catch {
      toast.error("Code invalide ou expiré.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-10">
      <div className="mb-4 flex items-center gap-2">
        <ShieldCheck size={20} style={{ color: GOLD }} />
        <h1 className="text-lg font-semibold">Suivi de votre dossier d'arbitrage</h1>
      </div>

      {step === "email" && (
        <div className="space-y-3 rounded-md border p-4" style={{ borderColor: BORDER }}>
          <p className="text-sm opacity-75">
            Saisissez l'adresse e-mail communiquée à votre conseil pour recevoir un
            code d'accès à usage unique.
          </p>
          <label className="block text-sm">
            <span style={{ color: GOLD }}>Adresse e-mail</span>
            <input className={inputCls} style={{ borderColor: BORDER }} type="email"
              value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <button type="button" disabled={busy} onClick={onAskCode}
            className="inline-flex items-center gap-1 rounded-md px-4 py-2 text-sm font-medium text-black disabled:opacity-50"
            style={{ background: GOLD }}>
            {busy && <Loader2 size={14} className="animate-spin" />} Recevoir un code
          </button>
        </div>
      )}

      {step === "code" && (
        <div className="space-y-3 rounded-md border p-4" style={{ borderColor: BORDER }}>
          <p className="text-sm opacity-75">
            Un code à 6 chiffres a été envoyé à <strong>{email}</strong> (valable 10 minutes).
          </p>
          <label className="block text-sm">
            <span style={{ color: GOLD }}>Code reçu</span>
            <input className={inputCls} style={{ borderColor: BORDER }} inputMode="numeric"
              value={code} onChange={(e) => setCode(e.target.value)} />
          </label>
          <button type="button" disabled={busy} onClick={onVerify}
            className="inline-flex items-center gap-1 rounded-md px-4 py-2 text-sm font-medium text-black disabled:opacity-50"
            style={{ background: GOLD }}>
            {busy && <Loader2 size={14} className="animate-spin" />} Valider
          </button>
          <button type="button" className="block text-xs underline opacity-70"
            onClick={() => setStep("email")}>
            Changer d'adresse
          </button>
        </div>
      )}

      {step === "data" && data && (
        <div className="space-y-4">
          <div className="rounded-md border p-4" style={{ borderColor: BORDER }}>
            <div className="text-xs uppercase tracking-wide" style={{ color: GOLD }}>Dossier</div>
            <div className="mt-1 font-mono text-sm">{data.reference}</div>
            <div className="mt-2 text-sm">{data.objet_litige}</div>
            <div className="mt-2 text-sm">
              État : <strong>{data.statut}</strong>
            </div>
          </div>

          <div className="rounded-md border p-4" style={{ borderColor: BORDER }}>
            <div className="text-xs uppercase tracking-wide" style={{ color: GOLD }}>
              Pièces ({data.pieces.length})
            </div>
            {data.pieces.length === 0 ? (
              <p className="mt-2 text-sm opacity-70">Aucune pièce pour le moment.</p>
            ) : (
              <ul className="mt-2 space-y-1 text-sm">
                {data.pieces.map((p, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <FileText size={14} style={{ color: GOLD }} />
                    <span className="truncate">{p.filename}</span>
                    {p.categorie && <span className="opacity-60">· {p.categorie}</span>}
                    {p.statut && <span className="ml-auto opacity-60">{p.statut}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <p className="text-[11px] opacity-60">
            Consultation en lecture seule. Aucune décision juridictionnelle n'est
            produite ici ; pour toute action, rapprochez-vous de votre conseil.
          </p>
        </div>
      )}
    </div>
  );
}
